import type { GtmInstance, GtmTrackEventParams } from "../types";
import { useRuntimeConfig } from "nuxt/app";
import { useGtm } from "./useGtm";

interface EcommerceProductCategory {
  translated?: { name?: string };
  name?: string;
}

interface EcommerceProductManufacturer {
  translated?: { name?: string };
  name?: string;
}

interface EcommercePriceEntry {
  gross?: number;
}

interface EcommerceProductPrice {
  unitPrice?: number;
}

interface EcommerceProduct {
  id?: string;
  productNumber?: string;
  name?: string;
  translated?: { name?: string };
  manufacturer?: EcommerceProductManufacturer;
  categories?: EcommerceProductCategory[];
  categoryTree?: string[] | string;
  variation?: string[];
  calculatedPrice?: EcommerceProductPrice & {
    listPrice?: { discount?: number };
  };
  price?: EcommercePriceEntry[];
}

interface EcommerceCartItemPrice {
  unitPrice?: number;
  calculatedTaxes?: { tax: number }[];
}

interface EcommerceCartItemPayloadOption {
  option: string;
}

interface EcommerceCartItemPayload {
  productNumber?: string;
  manufacturerName?: string;
  options?: EcommerceCartItemPayloadOption[];
  categoryIds?: string[];
  categoryNames?: string[];
  categoryTree?: string[];
}

interface EcommerceCartItem {
  id?: string;
  referencedId?: string;
  label?: string;
  quantity?: number;
  price?: EcommerceCartItemPrice;
  payload?: EcommerceCartItemPayload;
}

interface EcommerceCartPrice {
  totalPrice?: number;
}

interface EcommerceCart {
  lineItems?: EcommerceCartItem[];
  price?: EcommerceCartPrice;
  deliveries?: any[];
}

interface EcommerceOrder extends EcommerceCart {
  id?: string;
  orderNumber?: string;
}

interface GA4Item {
  item_id: string;
  item_name: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price: number;
  promotion_id?: string;
  promotion_name?: string;
  quantity?: number;
}

export interface UseGtmEcommerceOptions {
  affiliation?: string;
  currency?: string;
}

export const useGTMEcommerce = (options: UseGtmEcommerceOptions = {}) => {
  const runtimeConfig = useRuntimeConfig();
  const defaultAffiliation: string = String(
    runtimeConfig.public?.brandName || "",
  );
  const { affiliation = defaultAffiliation, currency = "EUR" } = options as {
    affiliation?: string;
    currency?: string;
  };
  const gtm: GtmInstance | null = useGtm();

  const clearEcommerce = () => {
    gtm?.trackEvent({ ecommerce: null } as any);
  };

  const pushEcommerceEvent = (params: GtmTrackEventParams) => {
    // GA4 via GTM expects ecommerce params under the `ecommerce` object
    const { event, ...rest } = params;
    clearEcommerce();
    gtm?.trackEvent({ event, ecommerce: rest } as any);
  };

  const mapProduct = (
    product: EcommerceProduct,
    args: {
      quantity?: number;
      index?: number;
      variant?: string;
      listId?: string;
      listName?: string;
      promotionId?: string;
      promotionName?: string;
    } = {},
  ): GA4Item => {
    const {
      quantity = 1,
      index = 0,
      variant,
      listId,
      listName,
      promotionId,
      promotionName,
    } = args;

    const categories = product.categories || [];

    return {
      item_id: product.id || product.productNumber || "",
      item_name: product.translated?.name || product.name || "",
      affiliation: affiliation || undefined,
      coupon: "",
      discount: product.calculatedPrice?.listPrice?.discount || 0,
      index,
      item_brand:
        product.manufacturer?.translated?.name ||
        product.manufacturer?.name ||
        "",
      item_category:
        categories?.[0]?.translated?.name || categories?.[0]?.name || "",
      item_category2:
        categories?.[1]?.translated?.name || categories?.[1]?.name || "",
      item_category3:
        categories?.[2]?.translated?.name || categories?.[2]?.name || "",
      item_category4:
        categories?.[3]?.translated?.name || categories?.[3]?.name || "",
      item_category5:
        categories?.[4]?.translated?.name || categories?.[4]?.name || "",
      item_list_id: listId,
      item_list_name: listName,
      item_variant: variant || product.variation?.join(", ") || "",
      location_id: "",
      price:
        product.calculatedPrice?.unitPrice || product.price?.[0]?.gross || 0,
      promotion_id: promotionId || undefined,
      promotion_name: promotionName || undefined,
      quantity,
    };
  };

  const mapCartItem = (cartItem: EcommerceCartItem, index = 0): GA4Item => {
    const categoryIds = cartItem?.payload?.categoryIds;
    const categoryNames = cartItem?.payload?.categoryNames;
    const categoryTree = cartItem?.payload?.categoryTree;

    const getCategory = (i: number) => {
      if (categoryNames && categoryNames[i]) return categoryNames[i];
      if (categoryTree && categoryTree[i]) return categoryTree[i];
      if (categoryIds && categoryIds[i]) return categoryIds[i];
      return "";
    };

    return {
      item_id:
        cartItem?.payload?.productNumber ||
        cartItem.referencedId ||
        cartItem.id ||
        "",
      item_name: cartItem.label || "",
      affiliation: affiliation || undefined,
      coupon: "",
      discount: cartItem.price?.calculatedTaxes?.[0]?.tax || 0,
      index,
      item_brand: cartItem?.payload?.manufacturerName || "",
      item_category: getCategory(0),
      item_category2: getCategory(1),
      item_category3: getCategory(2),
      item_category4: getCategory(3),
      item_category5: getCategory(4),
      item_list_id: "",
      item_list_name: "",
      item_variant:
        cartItem?.payload?.options?.map((opt: any) => opt.option).join(", ") ||
        "",
      location_id: "",
      price: cartItem.price?.unitPrice || 0,
      quantity: cartItem.quantity || 1,
    };
  };

  const mapCartItems = (cartItems: EcommerceCartItem[]) =>
    cartItems.map((item, index) => mapCartItem(item, index));

  const trackProductList = (
    products: EcommerceProduct[],
    listId: string,
    listName: string,
  ) => {
    const items = products.map((product, index) =>
      mapProduct(product, { index, listId, listName }),
    );
    pushEcommerceEvent({
      event: "view_item_list",
      item_list_id: listId,
      item_list_name: listName,
      items,
    });
  };

  /** GA4 recommended `view_search_results`: `search_term` on the event, `items` under `ecommerce`. */
  const trackViewSearchResults = (
    searchTerm: string,
    products: EcommerceProduct[],
  ) => {
    const listId = "search_results";
    const listName = "Search results";
    const items = products.map((product, index) =>
      mapProduct(product, { index, listId, listName }),
    );
    clearEcommerce();
    gtm?.trackEvent({
      event: "view_search_results",
      search_term: searchTerm,
      ecommerce: { items },
    } as any);
  };

  const trackProductClick = (
    product: EcommerceProduct,
    listId: string,
    listName: string,
    index = 0,
  ) => {
    const item = mapProduct(product, { index, listId, listName });
    pushEcommerceEvent({
      event: "select_item",
      item_list_id: listId,
      item_list_name: listName,
      items: [item],
    });
  };


    const trackOutOfStockMessage = (product: EcommerceProduct, listId: string, listName: string, index = 0) => {
        const item = mapProduct(product, { index, listId, listName });
        pushEcommerceEvent({
            event: 'item_out_of_stock',
            items: [item],
        });
    };

  const trackProductView = (product: EcommerceProduct, variant?: string) => {
    const item = mapProduct(product, { variant });
    const value = item.price * (item.quantity || 1);
    pushEcommerceEvent({ event: "view_item", currency, value, items: [item] });
  };

  const trackAddToCart = (
    product: EcommerceProduct,
    quantity = 1,
    variant?: string,
  ) => {
    const item = mapProduct(product, { quantity, variant });
    const value = item.price * (item.quantity || 1);
    pushEcommerceEvent({
      event: "add_to_cart",
      currency,
      value,
      items: [item],
    });
  };

  const trackRemoveFromCart = (
    product: EcommerceProduct,
    quantity = 1,
    variant?: string,
  ) => {
    const item = mapProduct(product, { quantity, variant });
    const value = item.price * (item.quantity || 1);
    pushEcommerceEvent({
      event: "remove_from_cart",
      currency,
      value,
      items: [item],
    });
  };

  const trackCartUpdate = (cartItems: EcommerceCartItem[]) => {
    const items = mapCartItems(cartItems);
    const totalValue = items.reduce(
      (sum, item) => sum + item.price * (item.quantity || 1),
      0,
    );
    return { items, totalValue };
  };

  const trackViewCart = (cartItems: EcommerceCartItem[]) => {
    const items = mapCartItems(cartItems || []);
    const value = items.reduce(
      (sum, item) => sum + item.price * (item.quantity || 1),
      0,
    );
    pushEcommerceEvent({ event: "view_cart", currency, value, items });
  };

  const trackAddToCartFromCartItem = (
    cartItem: EcommerceCartItem,
    quantity = 1,
  ) => {
    const item = mapCartItem(cartItem);
    const value = item.price * quantity;
    pushEcommerceEvent({
      event: "add_to_cart",
      currency,
      value,
      items: [{ ...item, quantity }],
    });
  };

  const trackRemoveFromCartFromCartItem = (
    cartItem: EcommerceCartItem,
    quantity = 1,
  ) => {
    const item = mapCartItem(cartItem);
    const value = item.price * quantity;
    pushEcommerceEvent({
      event: "remove_from_cart",
      currency,
      value,
      items: [{ ...item, quantity }],
    });
  };

  const trackBeginCheckout = (cart: EcommerceCart) => {
    const items = mapCartItems(cart?.lineItems || []);
    const value = cart?.price?.totalPrice || 0;
    const coupon =
      cart?.deliveries?.[0]?.shippingCosts?.extensions?.promocode?.code;
    pushEcommerceEvent({
      event: "begin_checkout",
      currency,
      value,
      items,
      ...(coupon ? { coupon } : {}),
    });
  };

  const trackShippingInfo = (cart: EcommerceCart, shippingMethod: string) => {
    const items = mapCartItems(cart?.lineItems || []);
    const value = cart?.price?.totalPrice || 0;
    const coupon =
      cart?.deliveries?.[0]?.shippingCosts?.extensions?.promocode?.code;
    pushEcommerceEvent({
      event: "add_shipping_info",
      currency,
      value,
      shipping_tier: shippingMethod,
      items,
      ...(coupon ? { coupon } : {}),
    });
  };

  const trackPaymentInfo = (cart: EcommerceCart, paymentMethod: string) => {
    const items = mapCartItems(cart?.lineItems || []);
    const value = cart?.price?.totalPrice || 0;
    const coupon =
      cart?.deliveries?.[0]?.shippingCosts?.extensions?.promocode?.code;
    pushEcommerceEvent({
      event: "add_payment_info",
      currency,
      value,
      payment_type: paymentMethod,
      items,
      ...(coupon ? { coupon } : {}),
    });
  };

  const trackPurchase = (order: EcommerceOrder) => {
    const items = mapCartItems(order?.lineItems || []);
    const transactionId = order?.orderNumber || order?.id || "";
    const value = order?.price?.totalPrice || 0;
    const tax =
      (order as any).price?.calculatedTaxes?.reduce(
        (sum: number, tax: any) => sum + tax.tax,
        0,
      ) || 0;
    const shipping = order.deliveries?.[0]?.shippingCosts?.totalPrice || 0;
    const coupon =
      order.deliveries?.[0]?.shippingCosts?.extensions?.promocode?.code;
    pushEcommerceEvent({
      event: "purchase",
      transaction_id: transactionId,
      currency,
      value,
      tax,
      shipping,
      items,
      ...(coupon ? { coupon } : {}),
    });
  };

  const trackRefund = (
    order: EcommerceOrder,
    refundItems?: EcommerceCartItem[],
    refundValue?: number,
  ) => {
    const transactionId = order.orderNumber || order.id || "";
    if (refundItems && refundItems.length > 0) {
      const items = mapCartItems(refundItems);
      const value =
        refundValue ||
        items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
      pushEcommerceEvent({
        event: "refund",
        transaction_id: transactionId,
        currency,
        value,
        items,
      });
    } else {
      pushEcommerceEvent({
        event: "refund",
        transaction_id: transactionId,
        currency,
        value: (order as any).price?.totalPrice,
      });
    }
  };

  const trackPromotionView = (products: EcommerceProduct[], promotion: any) => {
    const items = products.map((product) =>
      mapProduct(product, {
        promotionId: promotion.id,
        promotionName: promotion.name,
      }),
    );
    pushEcommerceEvent({
      event: "view_promotion",
      promotion_id: promotion.id,
      promotion_name: promotion.name,
      creative_name: promotion.creativeName,
      creative_slot: promotion.creativeSlot,
      items: items.map((item) => ({
        ...item,
        promotion_id: promotion.id,
        promotion_name: promotion.name,
      })),
    });
  };

  const trackPromotionClick = (product: EcommerceProduct, promotion: any) => {
    const item = mapProduct(product, {
      promotionId: promotion.id,
      promotionName: promotion.name,
    });
    pushEcommerceEvent({
      event: "select_promotion",
      promotion_id: promotion.id,
      promotion_name: promotion.name,
      creative_name: promotion.creativeName,
      creative_slot: promotion.creativeSlot,
      items: [
        { ...item, promotion_id: promotion.id, promotion_name: promotion.name },
      ],
    });
  };

  const trackAddToWishlist = (product: EcommerceProduct) => {
    const item = mapProduct(product);
    pushEcommerceEvent({
      event: "add_to_wishlist",
      currency,
      value: item.price,
      items: [item],
    });
  };

  const trackRemoveFromWishlist = (product: EcommerceProduct) => {
    const item = mapProduct(product);
    pushEcommerceEvent({
      event: "remove_from_wishlist",
      currency,
      value: item.price,
      items: [item],
    });
  };

  const trackNewsletterSignup = (method = "footer") => {
    pushEcommerceEvent({ event: "sign_up", method });
  };

  const trackContactForm = (formType = "contact") => {
    pushEcommerceEvent({ event: "generate_lead", form_type: formType });
  };

  const trackShare = (product: EcommerceProduct, method = "social") => {
    pushEcommerceEvent({
      event: "share",
      method,
      content_type: "product",
      content_id: product.id as string,
    });
  };

  return {
      // Product events
      trackProductList,
      trackViewSearchResults,
      trackProductClick,
      trackProductView,
      // Cart events
      trackAddToCart,
      trackRemoveFromCart,
      trackCartUpdate,
      trackViewCart,
      trackAddToCartFromCartItem,
      trackRemoveFromCartFromCartItem,
      // Checkout events
      trackBeginCheckout,
      trackShippingInfo,
      trackPaymentInfo,
      // Purchase events
      trackPurchase,
      trackRefund,
      // Content events
      // Promotion events
      trackPromotionView,
      trackPromotionClick,
      // Engagement events
      trackAddToWishlist,
      trackRemoveFromWishlist,
      trackNewsletterSignup,
      trackContactForm,
      trackShare,
      trackOutOfStockMessage,
  };
};
